import { useMemo } from "react";
import { Box } from "@repo/ui/core";
import { motion } from "framer-motion";

type FloatingPathsProps = {
  position: number;
  colorFrom: string;
  colorTo: string;
};

function FloatingPaths({ position, colorFrom, colorTo }: FloatingPathsProps) {
  const paths = useMemo(
    () =>
      Array.from({ length: 36 }, (_, i) => ({
        id: i,
        d: `M-${380 - i * 5 * position} -${189 + i * 6}C-${
          380 - i * 5 * position
        } -${189 + i * 6} -${312 - i * 5 * position} ${216 - i * 6} ${
          152 - i * 5 * position
        } ${343 - i * 6}C${616 - i * 5 * position} ${470 - i * 6} ${
          684 - i * 5 * position
        } ${875 - i * 6} ${684 - i * 5 * position} ${875 - i * 6}`,
        width: 0.5 + i * 0.03,
        duration: 20 + (i * 37 % 10),
      })),
    [position],
  );

  const gradientId = `path-gradient-${position > 0 ? "a" : "b"}`;

  return (
    <Box
      pos="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      style={{ pointerEvents: "none" }}
    >
      <svg width="100%" height="100%" viewBox="0 0 696 316" fill="none">
        <title>Background Paths</title>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={colorFrom} />
            <stop offset="100%" stopColor={colorTo} />
          </linearGradient>
        </defs>
        {paths.map((path) => (
          <motion.path
            key={path.id}
            d={path.d}
            stroke={`url(#${gradientId})`}
            strokeWidth={path.width}
            strokeOpacity={0.06 + path.id * 0.018}
            initial={{ pathLength: 0.3, opacity: 0.35 }}
            animate={{
              pathLength: 1,
              opacity: [0.2, 0.4, 0.2],
              pathOffset: [0, 1, 0],
            }}
            transition={{
              duration: path.duration,
              repeat: Number.POSITIVE_INFINITY,
              ease: "linear",
            }}
          />
        ))}
      </svg>
    </Box>
  );
}

type BackgroundPathsProps = {
  colorFrom: string;
  colorTo: string;
};

export function BackgroundPaths({ colorFrom, colorTo }: BackgroundPathsProps) {
  return (
    <Box
      pos="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      style={{ overflow: "hidden" }}
    >
      <FloatingPaths position={1} colorFrom={colorFrom} colorTo={colorTo} />
      <FloatingPaths position={-1} colorFrom={colorFrom} colorTo={colorTo} />
    </Box>
  );
}
